/**
 * useVersionCheck — detects when a newer frontend build has been deployed while
 * the current tab is still running an older one.
 *
 * Why this is needed: this is a react-router SPA, so `index.html` is fetched
 * exactly once (on the first hard load). All later navigation is client-side, so
 * a long-lived tab keeps running whatever hashed bundles it downloaded on day 1
 * and never re-checks the server — even though nginx serves those bundles as
 * `immutable`. The only way such a tab learns about a deploy is if we actively
 * poll a non-cached marker and compare it to what we booted with.
 *
 * Marker: CRA emits `asset-manifest.json` on every build and nginx serves it
 * `no-cache`. Its `main.js` entry is content-hashed, so it changes on every
 * build. We capture the value seen at startup as the baseline (it matches the
 * bundle currently executing) and compare subsequent polls against it. No build
 * or CI changes required.
 */
import { useEffect, useRef, useState } from 'react';

async function fetchBuildId(): Promise<string | null> {
  try {
    // cache: 'no-store' guarantees we bypass the HTTP cache even if an
    // intermediary ever mis-caches the manifest.
    const res = await fetch('/asset-manifest.json', { cache: 'no-store' });
    if (!res.ok) return null;
    const manifest = (await res.json()) as { files?: Record<string, string> };
    // The hashed main entry uniquely identifies a build.
    return manifest.files?.['main.js'] ?? null;
  } catch {
    // Network blips must not surface as false "update available" prompts.
    return null;
  }
}

/**
 * Returns true once a build newer than the one this tab loaded is detected.
 * @param pollMs how often to poll while the tab is open (default 60s).
 */
export function useVersionCheck(pollMs = 60_000): boolean {
  const baseline = useRef<string | null>(null);
  const [updateAvailable, setUpdateAvailable] = useState(false);

  useEffect(() => {
    let stopped = false;

    const check = async () => {
      if (stopped || updateAvailable) return;
      const current = await fetchBuildId();
      if (stopped || current == null) return;
      if (baseline.current == null) {
        baseline.current = current;
      } else if (current !== baseline.current) {
        setUpdateAvailable(true);
      }
    };

    check();
    const intervalId = window.setInterval(check, pollMs);

    // Also check the moment the user returns to the tab — covers laptops waking
    // from sleep, where the interval may have been throttled/paused.
    const onVisible = () => {
      if (document.visibilityState === 'visible') check();
    };
    window.addEventListener('focus', onVisible);
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      stopped = true;
      window.clearInterval(intervalId);
      window.removeEventListener('focus', onVisible);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [pollMs, updateAvailable]);

  return updateAvailable;
}
